import type { ReactNode } from 'react';
import {
  CaretRightIcon,
  CarProfileIcon,
  MapPinIcon,
  SnowflakeIcon,
  SparkleIcon,
  SteeringWheelIcon,
} from '@phosphor-icons/react';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import { OutOfPolicyGlyph } from '../../policy/out_of_policy_mark.js';
import { OfferBadge } from '../../shared/results_page/offer_badge.js';
import type { RentalOfferView, RentalVehicleView } from '../models.js';
import { RentalCompanyLogo } from '../rental_company_mark.js';
import './rental_offer_card.css';

/** Os textos do miolo do card — o que a listagem e o checkout dividem. */
export type RentalVehicleCopy = {
  automaticLabel: string;
  /** O automático abreviado ("Autom.") do card do celular, só à vista: o
   *  leitor de tela continua ouvindo `automaticLabel` (#5643). */
  automaticShortLabel: string;
  manualLabel: string;
  airConditioningLabel: string;
  noAirConditioningLabel: string;
  /** Os papéis dos dois locais ("Retirada" / "Devolução"), só para o leitor
   *  de tela: à vista o local é o valor. */
  pickupLabel: string;
  dropoffLabel: string;
};

export type RentalOfferCardCopy = RentalVehicleCopy & {
  /** O nome do gesto do card ("Selecionar"), com o nome da oferta anexado. */
  selectLabel: string;
};

/** O peso dos glifos: o card do celular (#5125) e a prévia do mapa usam o
 *  bold; o card de desktop, o Regular do Figma (#5529). */
type GlyphWeight = 'regular' | 'bold';

/** Os dois desenhos do card: a linha do desktop e o card v2 do celular. */
type RentalOfferCardLayout = 'wide' | 'stack';

/** Quando a foto carrega: `lazy` numa lista longa; `eager` onde o card fica
 *  fora de vista sem nunca entrar nela pela rolagem da página — os cards de um
 *  trilho horizontal (o carrossel do chat), que só entram pelo swipe. */
type RentalPhotoLoading = 'lazy' | 'eager';

export type RentalOfferCardProps = {
  offer: RentalOfferView;
  copy: RentalOfferCardCopy;
  /** A oferta em curso no checkout — o card fica marcado (`aria-current`). */
  selected?: boolean;
  /** Uma escrita em voo (a cotação, a reserva): o botão trava. */
  busy?: boolean;
  /** O desenho, pelo palco (`NARROW_STAGE_QUERY`, que a lista lê): `wide` é a
   *  linha do desktop, `stack` o card v2 do celular (#5125). */
  layout?: RentalOfferCardLayout;
  /** Ver `RentalPhotoLoading`. Padrão `lazy`. */
  photoLoading?: RentalPhotoLoading;
  onSelect(offerId: string): void;
};

/**
 * A foto do veículo com queda macia: uma `<img>` quebrada pintaria o `alt` cru
 * dentro da caixa. A queda é a caixa bege com o carro de perfil. Decorativa
 * (`alt=""`): a foto repete o que o título já diz. A falha é da URL: a lista
 * reusa a posição entre buscas, e a PRÓXIMA oferta ganha a sua tentativa.
 */
export function RentalPhoto({
  url,
  weight = 'bold',
  loading = 'lazy',
}: {
  readonly url: string | undefined;
  readonly weight?: GlyphWeight;
  readonly loading?: RentalPhotoLoading;
}): ReactNode {
  const image = useImageFallback(url);
  if (!image.src) {
    return (
      <span className="hui-rental-offer-photo hui-rental-offer-photo--fallback" aria-hidden="true">
        <CarProfileIcon weight={weight} />
      </span>
    );
  }
  return (
    <img
      className="hui-rental-offer-photo"
      src={image.src}
      alt=""
      loading={loading}
      onError={image.onError}
    />
  );
}

/** O câmbio do chip. No card do celular o automático lê abreviado, por falta
 *  de espaço no chat (#5643); a palavra inteira fica para o leitor de tela. */
function transmissionLabel(
  offer: RentalVehicleView,
  copy: RentalVehicleCopy,
  abbreviated: boolean,
): ReactNode {
  if (offer.transmission !== 'automatic') return copy.manualLabel;
  if (!abbreviated) return copy.automaticLabel;
  return (
    <>
      <span className="sr-only">{copy.automaticLabel}</span>
      <span aria-hidden="true">{copy.automaticShortLabel}</span>
    </>
  );
}

/** Câmbio e ar-condicionado como chips de spec, cada um com o glifo do fato. O
 *  ar SEMPRE aparece — com ou sem — para "sem ar" nunca se confundir com "não
 *  informado" (`airConditioning` é booleano, não opcional). */
export function RentalSpecs({
  offer,
  copy,
  abbreviated = false,
  weight = 'bold',
}: {
  readonly offer: RentalVehicleView;
  readonly copy: RentalVehicleCopy;
  /** O automático abreviado, do card do celular. */
  readonly abbreviated?: boolean;
  readonly weight?: GlyphWeight;
}): ReactNode {
  return (
    <ul className="hui-rental-offer-specs">
      <li>
        <SteeringWheelIcon aria-hidden="true" weight={weight} />
        {transmissionLabel(offer, copy, abbreviated)}
      </li>
      <li data-muted={offer.airConditioning ? undefined : 'true'}>
        <SnowflakeIcon aria-hidden="true" weight={weight} />
        {offer.airConditioning ? copy.airConditioningLabel : copy.noAirConditioningLabel}
      </li>
    </ul>
  );
}

/** As datas das duas pontas numa linha, "retirada → devolução", sob o local —
 *  só onde a tela é o contrato (o checkout); a listagem não as tem. */
function StopDates({
  offer,
  copy,
}: {
  readonly offer: RentalVehicleView;
  readonly copy: RentalVehicleCopy;
}): ReactNode {
  const ends = [
    { label: copy.pickupLabel, when: offer.pickup.whenLabel },
    { label: copy.dropoffLabel, when: offer.dropoff.whenLabel },
  ].filter((end) => end.when);
  if (ends.length === 0) return null;
  return (
    <p className="hui-rental-offer-when">
      {ends.map(({ label, when }, index) => (
        <span key={label}>
          {index > 0 && <span aria-hidden="true">{' → '}</span>}
          <span className="sr-only">{`${label}: `}</span>
          {when}
        </span>
      ))}
    </p>
  );
}

/** O local no card de desktop, numa linha só e sem rótulos à vista (o leitor
 *  de tela ainda os ouve): a mesma agência nas duas pontas é o pino e UM valor;
 *  agências diferentes, "retirada ── devolução". Faltando espaço, as duas
 *  reticenciam na proporção do texto (#5529, #6390). O endereço inteiro mora
 *  no `title`. */
function RentalWideStops({
  offer,
  copy,
}: {
  readonly offer: RentalVehicleView;
  readonly copy: RentalVehicleCopy;
}): ReactNode {
  const same = offer.pickup.agencyLabel === offer.dropoff.agencyLabel;
  const place = (
    <p className="hui-rental-offer-place">
      <MapPinIcon aria-hidden="true" />
      {same ? (
        <span className="hui-rental-offer-place-value" title={offer.pickup.addressLabel}>
          <span className="sr-only">{`${copy.pickupLabel} · ${copy.dropoffLabel}: `}</span>
          {offer.pickup.agencyLabel}
        </span>
      ) : (
        <>
          <span
            className="hui-rental-offer-place-value"
            data-side="pickup"
            title={offer.pickup.addressLabel}
          >
            <span className="sr-only">{`${copy.pickupLabel}: `}</span>
            {offer.pickup.agencyLabel}
          </span>
          <span className="hui-rental-offer-place-link" aria-hidden="true" />
          <span
            className="hui-rental-offer-place-value"
            data-side="dropoff"
            title={offer.dropoff.addressLabel}
          >
            <span className="sr-only">{`; ${copy.dropoffLabel}: `}</span>
            {offer.dropoff.agencyLabel}
          </span>
        </>
      )}
    </p>
  );
  return (
    <>
      {place}
      <StopDates offer={offer} copy={copy} />
    </>
  );
}

/** O local no card v2, sem rótulos à vista (o leitor de tela ainda os ouve): a
 *  mesma agência nas duas pontas é UMA linha com o pino; agências diferentes,
 *  a pilha origem → destino, cujos marcadores são desenho (CSS). Com as datas
 *  (o checkout), cada ponta ganha a sua sob a agência — na mesma agência, as
 *  duas numa linha só, sob o pino. */
function RentalStackStops({
  offer,
  copy,
}: {
  readonly offer: RentalVehicleView;
  readonly copy: RentalVehicleCopy;
}): ReactNode {
  const { pickup, dropoff } = offer;
  if (pickup.agencyLabel === dropoff.agencyLabel) {
    const place = (
      <p className="hui-rental-offer-route" data-same="true">
        <MapPinIcon aria-hidden="true" weight="bold" />
        <span className="sr-only">{`${copy.pickupLabel} · ${copy.dropoffLabel}: `}</span>
        <span className="hui-rental-offer-route-value" title={pickup.addressLabel}>
          {pickup.agencyLabel}
        </span>
      </p>
    );
    if (!pickup.whenLabel && !dropoff.whenLabel) return place;
    return (
      <div className="hui-rental-offer-route-group">
        {place}
        <StopDates offer={offer} copy={copy} />
      </div>
    );
  }
  return (
    <ol className="hui-rental-offer-route">
      {[
        { label: copy.pickupLabel, stop: pickup },
        { label: copy.dropoffLabel, stop: dropoff },
      ].map(({ label, stop }) => (
        <li key={label} className="hui-rental-offer-route-stop">
          <span className="hui-rental-offer-route-value">
            <span className="sr-only">{`${label}: `}</span>
            <span title={stop.addressLabel}>{stop.agencyLabel}</span>
          </span>
          {stop.whenLabel && <span className="hui-rental-offer-when">{stop.whenLabel}</span>}
        </li>
      ))}
    </ol>
  );
}

/** Categoria e modelos. O separador é um nó de TEXTO próprio, não `::before` no
 *  CSS nem parte do span: conteúdo gerado entrava no nome acessível sem o
 *  espaço ("Econômico ·Fiat Argo"), e espaço na ponta do span é aparado pelo
 *  cálculo do nome (#3609 C4). Nos dois desenhos os modelos descem para a
 *  linha de baixo e o separador some de vista pelo tamanho de fonte zero do
 *  título — continua no nome do h3. O `title` guarda os modelos inteiros, que
 *  o desktop corta numa linha. */
function RentalTitle({ offer }: { readonly offer: RentalVehicleView }): ReactNode {
  return (
    <h3 className="hui-rental-offer-title">
      <strong>{offer.categoryLabel}</strong>
      {offer.vehicleLabel && (
        <>
          {' · '}
          <span title={offer.vehicleLabel}>{offer.vehicleLabel}</span>
        </>
      )}
    </h3>
  );
}

/**
 * O miolo do card — mídia, título, specs e local — nos dois desenhos, sem
 * selos, nota nem ação. A listagem o embrulha no card da oferta e põe o preço
 * em `price` (ao lado do título, no desktop); o checkout o usa sozinho como o
 * contrato do carro, sem preço e com as datas no local.
 */
export function RentalVehicleSummary({
  vehicle,
  copy,
  layout = 'wide',
  price,
  photoLoading,
}: {
  readonly vehicle: RentalVehicleView;
  readonly copy: RentalVehicleCopy;
  readonly layout?: RentalOfferCardLayout;
  /** O preço da linha 1 do desktop; o celular o mostra no rodapé do card. */
  readonly price?: ReactNode;
  readonly photoLoading?: RentalPhotoLoading;
}): ReactNode {
  if (layout === 'stack') {
    return (
      <div className="hui-rental-offer-main" data-layout="stack">
        <div className="hui-rental-offer-copy">
          <RentalTitle offer={vehicle} />
          <RentalSpecs offer={vehicle} copy={copy} abbreviated />
          <RentalStackStops offer={vehicle} copy={copy} />
        </div>
        <div className="hui-rental-offer-media">
          <RentalCompanyLogo company={vehicle.company} />
          <RentalPhoto url={vehicle.thumbnailUrl} loading={photoLoading} />
        </div>
      </div>
    );
  }
  return (
    <div className="hui-rental-offer-main" data-layout="wide">
      <div className="hui-rental-offer-media">
        {/* A caixa 72×20 da logo, nas três quedas (banco, imagem do
            fornecedor, chip de texto): utilitário no contêiner, porque o
            CSS de componente não vence os utilitários da logo. */}
        <span className="hui-rental-offer-brand [&>img]:h-auto [&>img]:max-h-5 [&>img]:max-w-full [&>span]:h-5 [&>span]:leading-5">
          <RentalCompanyLogo company={vehicle.company} />
        </span>
        <RentalPhoto url={vehicle.thumbnailUrl} weight="regular" loading={photoLoading} />
      </div>
      <div className="hui-rental-offer-copy">
        <div className="hui-rental-offer-head">
          <RentalTitle offer={vehicle} />
          {price}
        </div>
        <RentalSpecs offer={vehicle} copy={copy} weight="regular" />
        <RentalWideStops offer={vehicle} copy={copy} />
      </div>
    </div>
  );
}

/** O texto inteiro da oferta, que o botão sobre o card leva no `title`: ele
 *  cobre os textos cortados (modelos, locais), e o `title` deles não chegaria
 *  ao ponteiro. O local vai uma vez quando as duas pontas são a mesma. */
function hoverTitleOf(offer: RentalOfferView, title: string): string {
  const { pickup, dropoff } = offer;
  const place =
    pickup.agencyLabel === dropoff.agencyLabel
      ? pickup.agencyLabel
      : `${pickup.agencyLabel} → ${dropoff.agencyLabel}`;
  return [title, place].filter(Boolean).join(' · ');
}

function SelectButton({
  offer,
  copy,
  title,
  busy,
  onSelect,
}: {
  readonly offer: RentalOfferView;
  readonly copy: RentalOfferCardCopy;
  readonly title: string;
  readonly busy: boolean;
  readonly onSelect: (offerId: string) => void;
}): ReactNode {
  if (!offer.selectable) return null;
  return (
    // Um `<button>` cru, não o `Button` do ui-kit: ele é a CAMADA do clique
    // sobre o card, e os utilitários de altura do átomo (`h-7`) venciam o
    // `inset: 0` do CSS de componente — só a faixa de cima do card respondia.
    // O texto segue dentro, invisível (`opacity: 0`), como no `Button` de antes.
    <button
      type="button"
      className="hui-rental-offer-select"
      aria-label={`${copy.selectLabel} — ${title}`}
      title={hoverTitleOf(offer, title)}
      disabled={busy}
      onClick={() => {
        onSelect(offer.id);
      }}
    >
      {copy.selectLabel}
    </button>
  );
}

/** A nota de mérito. No card do celular ela pesa como conteúdo, igual ao
 *  insight do card de voo (`text-ui` em `foreground`, faísca 16 na primeira
 *  linha), e respira 16 do bloco principal: o vão de 12 do card mais 4 (#5643). */
function MeritNote({
  note,
  stack = false,
}: {
  readonly note: string | undefined;
  readonly stack?: boolean;
}): ReactNode {
  if (!note) return null;
  if (stack) {
    return (
      <p className="mt-1 mb-0 flex min-w-0 gap-1 text-ui text-foreground">
        <SparkleIcon size={16} weight="fill" className="mt-0.5 shrink-0" aria-hidden="true" />
        {note}
      </p>
    );
  }
  return (
    <p className="hui-rental-offer-merit">
      <SparkleIcon aria-hidden="true" weight="fill" />
      <span>{note}</span>
    </p>
  );
}

/**
 * O card v2 do celular (#5125): título, specs e local à esquerda; logo e foto
 * 108×80 à direita; a nota; o rodapé com a diária e o total. Fora da política
 * é só o escudo, numa pílula no canto de cima. O `Selecionar` — o mesmo botão,
 * com o mesmo nome — cobre o card inteiro: o toque no card é o gesto.
 */
function RentalStackBody({
  offer,
  copy,
  title,
  busy,
  photoLoading,
  onSelect,
}: {
  readonly offer: RentalOfferView;
  readonly copy: RentalOfferCardCopy;
  readonly title: string;
  readonly busy: boolean;
  readonly photoLoading: RentalPhotoLoading | undefined;
  readonly onSelect: (offerId: string) => void;
}): ReactNode {
  return (
    <>
      {offer.outOfPolicyLabel && (
        <span className="hui-rental-offer-policy">
          <OutOfPolicyGlyph label={offer.outOfPolicyLabel} />
        </span>
      )}
      <RentalVehicleSummary
        vehicle={offer}
        copy={copy}
        layout="stack"
        photoLoading={photoLoading}
      />
      <MeritNote note={offer.meritNote} stack />
      <p className="hui-rental-offer-footer">
        {offer.priceDetail && <small>{offer.priceDetail}</small>}
        <strong>
          {offer.priceLabel}
          <CaretRightIcon aria-hidden="true" />
        </strong>
      </p>
      <SelectButton offer={offer} copy={copy} title={title} busy={busy} onSelect={onSelect} />
    </>
  );
}

/** O card de desktop (#5529): logo e foto à esquerda; categoria e total na
 *  linha 1, modelos e diária na linha 2; specs e o local numa linha; a nota de
 *  mérito no pé, só com selo — a nota explica o selo. O `Selecionar` cobre o
 *  card inteiro, como no celular. */
function RentalWideBody({
  offer,
  copy,
  title,
  busy,
  photoLoading,
  onSelect,
}: {
  readonly offer: RentalOfferView;
  readonly copy: RentalOfferCardCopy;
  readonly title: string;
  readonly busy: boolean;
  readonly photoLoading: RentalPhotoLoading | undefined;
  readonly onSelect: (offerId: string) => void;
}): ReactNode {
  return (
    <>
      <RentalVehicleSummary
        vehicle={offer}
        copy={copy}
        photoLoading={photoLoading}
        price={
          <p className="hui-rental-offer-price">
            <strong>
              {/* O escudo de fora-da-política vem SEM PALAVRA e colado no
                  preço (a mesma régua do card do hotel): é onde a decisão
                  acontece. */}
              {offer.outOfPolicyLabel && <OutOfPolicyGlyph label={offer.outOfPolicyLabel} />}
              {offer.priceLabel}
            </strong>
            {offer.dailyPriceLabel && <small>{offer.dailyPriceLabel}</small>}
          </p>
        }
      />
      {offer.badges.length > 0 && <MeritNote note={offer.meritNote} />}
      <SelectButton offer={offer} copy={copy} title={title} busy={busy} onSelect={onSelect} />
    </>
  );
}

/**
 * O card de uma oferta de carro na página determinística de resultados, em dois
 * desenhos que diferem em ÁRVORE, não só em estilo — por isso a lista escolhe
 * pelo palco (`layout`), como o hotel, e só um deles existe no DOM: o card de
 * desktop (#5529) e o card v2 do celular (#5125). Os selos de eixo
 * interrompem a borda de cima nos dois.
 *
 * É um `<article>` com o gesto num botão só, `Selecionar`, esticado e
 * invisível sobre o card inteiro: o clique no card é o gesto, como no hotel, e
 * o botão leva o nome da oferta anexado (numa lista, "Selecionar" sozinho não
 * diz qual). Sem detalhe intermediário: o Selecionar vai direto ao checkout.
 */
export function RentalOfferCard({
  offer,
  copy,
  selected = false,
  busy = false,
  layout = 'wide',
  photoLoading,
  onSelect,
}: Readonly<RentalOfferCardProps>): ReactNode {
  const title = [offer.categoryLabel, offer.vehicleLabel].filter(Boolean).join(' · ');
  const Body = layout === 'stack' ? RentalStackBody : RentalWideBody;
  return (
    <article
      className="hui-rental-offer-card"
      data-layout={layout}
      aria-label={title}
      aria-current={selected || undefined}
    >
      {offer.badges.length > 0 && (
        // No celular o selo é Geist SemiBold 14 numa pílula de 24 (#5125), um
        // degrau acima do `Badge` do ui-kit: utilitário no contêiner, porque o
        // CSS de componente não vence os utilitários do átomo. No desktop é o
        // `Badge` de 20 como veio (#5529).
        <div
          className={
            layout === 'stack'
              ? 'hui-rental-offer-badges [&>*]:h-6 [&>*]:text-sm [&>*]:leading-5 [&>*]:font-semibold'
              : 'hui-rental-offer-badges'
          }
        >
          {offer.badges.map((badge) => (
            <OfferBadge key={badge.id} badge={badge} />
          ))}
        </div>
      )}
      <Body
        offer={offer}
        copy={copy}
        title={title}
        busy={busy}
        photoLoading={photoLoading}
        onSelect={onSelect}
      />
    </article>
  );
}
